import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getRideFeeRules, previewRideFee, getRideCashExceptions, resolveRideCashException } from '../api/admin';
import RideFeeForm from '../components/fees/RideFeeForm';
import { useCountry } from '../context/CountryContext';

// Fees & Taxes → Ride fees (Kayd, 28.10.2026 — driver dashboard spec,
// section 3): per country + Boda-Boda / Auto the driver's tariff and the
// NULA MOVE fee ADDED on top (customer pays both). Each change is a new
// version; rides keep the version they were booked under. Where no rule
// exists, rides keep the previous pricing. Also: cash trips whose two
// confirmations are missing (documented follow-up, no money entries).
const money = (c, cur) => `${cur || ''} ${((Number(c) || 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`.trim();
const day = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const CAT = { boda_boda: 'Boda-Boda', auto: 'Auto' };
const ROUND = { round_half_up: 'Nearest whole unit', round_down: 'Down to whole unit', round_up: 'Up to whole unit' };

export default function RideFees() {
  const { country } = useCountry();
  const [rules, setRules] = useState(null);
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [check, setCheck] = useState({ country: country && country !== 'ALL' ? country : 'KE', category: 'boda_boda', km: '6' });
  const [preview, setPreview] = useState(null);
  const [cash, setCash] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await getRideFeeRules(country && country !== 'ALL' ? { country } : {});
      setRules(r.data?.rules || []);
      setSetupNeeded(!!r.data?.setup_needed);
    } catch (e) {
      setRules([]);
      toast.error(e.response?.data?.error || 'Failed to load ride fees');
    }
    getRideCashExceptions().then((r) => setCash(r.data?.rides || [])).catch(() => setCash([]));
  }, [country]);
  useEffect(() => { load(); }, [load]);

  const runCheck = async () => {
    try {
      const r = await previewRideFee({ country: check.country, category: check.category, km: check.km });
      setPreview(r.data?.price || null);
    } catch (e) {
      setPreview(null);
      toast.error(e.response?.data?.error || 'Could not calculate');
    }
  };

  const resolve = async (ride, outcome) => {
    const note = window.prompt(outcome === 'not_paid'
      ? `Record trip ${ride.trip_number} as NOT PAID? No earnings or fee are recorded.\nPlease describe what was confirmed (saved in the log):`
      : `Add a follow-up note for trip ${ride.trip_number} (who was contacted, what they said):`);
    if (!note || !note.trim()) return;
    try {
      await resolveRideCashException(ride.id, { outcome, note });
      toast.success('Saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save');
    }
  };

  return (
    <div>
      <div style={s.head}>
        <div>
          <h2 style={s.title}>Ride fees (Boda-Boda &amp; Auto)</h2>
          <p style={s.hint}>
            The NULA MOVE fee is <strong>added</strong> to the driver's trip amount — e.g. driver amount KES 450 + 10 % fee KES 45 = customer pays KES 495, and the
            driver earns the full KES 450 (the fee is never deducted again as commission). Percentages always use the driver's trip amount. Each change creates a new
            version from its “effective from” time; booked rides keep their saved price. Countries / categories without a rule keep the previous ride pricing.
            Before activating a rule, please confirm that this fee structure is permitted in the country and which taxes must be included.
          </p>
        </div>
        <button type="button" style={s.btnPrimary} onClick={() => setShowForm((v) => !v)}>{showForm ? 'Close form' : '+ New rule / version'}</button>
      </div>
      {setupNeeded && <div style={s.warn}>The database update for ride fees has not been run yet.</div>}
      {/* A rule that only starts in the future is not used for bookings yet;
          a rule without a waiting rounding rule charges no waiting time. */}
      {(rules || []).filter((r) => r.scheduled && !(rules || []).some((x) => x.in_force && x.country_code === r.country_code && x.vehicle_category === r.vehicle_category)).map((r) => (
        <div key={`sched-${r.id}`} style={s.warn} role="alert">
          {r.country_code} · {CAT[r.vehicle_category] || r.vehicle_category}: rule {r.rule_version} only starts on <strong>{day(r.effective_from)}</strong>. Until then there is no ride price for this category (no booking price, no cash option). Please check the date.
        </div>
      ))}
      {(rules || []).filter((r) => (r.in_force || r.scheduled) && r.wait_rate_cents_per_minute != null && !r.wait_rounding).map((r) => (
        <div key={`round-${r.id}`} style={s.warn} role="alert">
          {r.country_code} · {CAT[r.vehicle_category] || r.vehicle_category}: rule {r.rule_version} has a waiting rate but no rounding rule (per second / per started minute), so no waiting time is charged. Please create a new version with the rounding rule.
        </div>
      ))}
      {showForm && <RideFeeForm defaultCountry={check.country} onSaved={() => { load(); setShowForm(false); }} />}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>{['Version', 'Country', 'Category', 'Driver tariff', 'NULA MOVE fee', 'Waiting / no-show', 'Rounding', 'Effective from', 'Status', 'Reason'].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {rules === null && <tr><td colSpan={10} style={s.td}>Loading…</td></tr>}
            {rules && rules.length === 0 && <tr><td colSpan={10} style={s.td}>No ride fee rules yet — rides use the previous pricing.</td></tr>}
            {(rules || []).map((r) => (
              <tr key={r.id}>
                <td style={s.td}><strong>{r.rule_version}</strong></td>
                <td style={s.td}>{r.country_code} · {r.currency}</td>
                <td style={s.td}>{CAT[r.vehicle_category] || r.vehicle_category}</td>
                <td style={s.td}>{money(r.base_fare_cents, r.currency)} + {money(r.per_km_cents, r.currency)}/km<div style={s.muted}>minimum {money(r.minimum_fare_cents, r.currency)}</div></td>
                <td style={s.td}>{r.fee_type === 'percent' ? `${Number(r.fee_value)} % of driver amount` : `${money(Number(r.fee_value) * 100, r.currency)} per trip`}</td>
                <td style={s.td}>{Number(r.wait_free_seconds || 0) / 60} free min · {money(r.wait_rate_cents_per_minute, r.currency)}/min<div style={s.muted}>{r.no_show_enabled ? `no-show after ${Number(r.wait_no_show_limit_seconds || 0) / 60} min` : 'no-show off'}</div></td>
                <td style={s.td}>{ROUND[r.rounding] || r.rounding}</td>
                <td style={s.td}>{day(r.effective_from)}</td>
                <td style={s.td}><span style={{ ...s.badge, ...(r.in_force ? s.badgeOn : r.scheduled ? s.badgeWarn : {}) }}>{r.in_force ? 'In force' : r.scheduled ? 'Scheduled' : 'Replaced'}</span></td>
                <td style={s.td}>{r.note || '—'}<div style={s.muted}>{r.creator?.full_name || ''}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={s.box}>
        <h3 style={s.h3}>Check a price</h3>
        <div style={s.row}>
          <label style={s.label}>Country<input style={s.input} value={check.country} maxLength={3} onChange={(e) => setCheck({ ...check, country: e.target.value.toUpperCase() })} /></label>
          <label style={s.label}>Category
            <select style={s.input} value={check.category} onChange={(e) => setCheck({ ...check, category: e.target.value })}>
              <option value="boda_boda">Boda-Boda</option><option value="auto">Auto</option>
            </select>
          </label>
          <label style={s.label}>Distance (km)<input style={s.input} type="number" min="0.1" step="0.1" value={check.km} onChange={(e) => setCheck({ ...check, km: e.target.value })} /></label>
          <button type="button" style={{ ...s.btnPrimary, alignSelf: 'flex-end' }} onClick={runCheck}>Calculate</button>
        </div>
        {preview && (
          <div style={s.result}>
            <div>Driver's trip amount: <strong>{money(preview.driver_trip_amount_cents, preview.currency)}</strong></div>
            <div>NULA MOVE fee: <strong>{money(preview.nula_fee_cents, preview.currency)}</strong></div>
            <div>Customer pays: <strong>{money(preview.customer_total_cents, preview.currency)}</strong></div>
            <div style={s.muted}>Rule {preview.rule_version}</div>
          </div>
        )}
      </div>

      <div style={s.box}>
        <h3 style={s.h3}>Cash trips waiting for confirmation</h3>
        <p style={s.hint}>
          A trip that has ended is never left open: after 2 hours without both confirmations it shows “Trip ended · Cash payment unresolved”. Staff follow up and record
          notes here. Staff cannot confirm for the customer or the driver, and no earnings or fees are posted until both have confirmed — or the trip is recorded as not paid.
        </p>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr>{['Trip', 'Ended', 'Amount', 'Customer confirmed', 'Driver confirmed', 'Status', 'Notes', ''].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {cash === null && <tr><td colSpan={8} style={s.td}>Loading…</td></tr>}
              {cash && cash.length === 0 && <tr><td colSpan={8} style={s.td}>No open cash trips.</td></tr>}
              {(cash || []).map((r) => (
                <tr key={r.id}>
                  <td style={s.td}><strong>{r.trip_number}</strong><div style={s.muted}>{r.customer?.full_name || '—'} · driver {r.driver?.full_name || '—'}</div></td>
                  <td style={s.td}>{day(r.ended_at)}</td>
                  <td style={s.td}>{money(r.customer_total_cents, r.fee_currency)}<div style={s.muted}>driver {money(r.driver_trip_amount_cents, r.fee_currency)} · fee {money(r.nula_fee_cents, r.fee_currency)}</div></td>
                  <td style={s.td}>{r.customer_cash_confirmed_at ? `✓ ${day(r.customer_cash_confirmed_at)}` : 'Not yet'}</td>
                  <td style={s.td}>{r.driver_cash_confirmed_at ? `✓ ${day(r.driver_cash_confirmed_at)}` : 'Not yet'}</td>
                  <td style={s.td}><span style={{ ...s.badge, ...(r.cash_status === 'unresolved' ? s.badgeRed : s.badgeWarn) }}>{r.cash_status === 'unresolved' ? 'Trip ended · Cash payment unresolved' : 'Waiting'}</span></td>
                  <td style={s.td}>{(r.cash_resolution_log || []).map((l, i) => <div key={i} style={s.muted}>{day(l.at)} · {l.note}</div>)}</td>
                  <td style={s.tdA}>
                    <button type="button" style={s.link} onClick={() => resolve(r, 'note')}>Add note</button>
                    <button type="button" style={s.linkRed} onClick={() => resolve(r, 'not_paid')}>Not paid</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const s = {
  head: { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap' },
  title: { margin: 0, fontSize: 18, fontWeight: 800 },
  h3: { margin: '0 0 8px', fontSize: 15.5, fontWeight: 800 },
  hint: { margin: '4px 0 8px', color: '#4b5563', fontSize: 13, maxWidth: 820, lineHeight: 1.55 },
  warn: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: 12, fontSize: 13.5, marginBottom: 12 },
  btnPrimary: { padding: '9px 14px', background: '#0B6A4A', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  tableWrap: { overflowX: 'auto', WebkitOverflowScrolling: 'touch', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 900, fontSize: 13 },
  th: { textAlign: 'left', padding: '9px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  tdA: { padding: '6px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top', whiteSpace: 'nowrap' },
  muted: { fontSize: 12, color: '#6b7280' },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' },
  badgeOn: { background: '#dcfce7', color: '#166534' },
  badgeWarn: { background: '#fef3c7', color: '#92400e' },
  badgeRed: { background: '#fee2e2', color: '#991b1b' },
  box: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginTop: 16 },
  row: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, fontWeight: 700, color: '#374151', flex: '1 1 130px', maxWidth: 220 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, background: '#fff', minHeight: 38, boxSizing: 'border-box' },
  result: { marginTop: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: 10, fontSize: 14, display: 'flex', flexDirection: 'column', gap: 3 },
  link: { background: 'none', border: 'none', color: '#0B6A4A', fontWeight: 700, cursor: 'pointer', padding: '2px 6px', fontSize: 13 },
  linkRed: { background: 'none', border: 'none', color: '#b91c1c', fontWeight: 700, cursor: 'pointer', padding: '2px 6px', fontSize: 13 },
};
